import { getIsLoggedIn, getIsProductsGridView } from '@app/ducks';
import { getOrderDetails } from '@app/ducks/order';
import { getUrlForProductAndVariant } from '@app/logic/products';
import { paths } from '@app/paths';
import { useAmplitude } from '@app/utils/analytics/useAmplitude';
import { createAbsoluteUrl } from '@app/utils/createAbsoluteUrl';
import { useAllProductsLink } from '@app/utils/hooks';
import { isEmpty } from '@app/utils/isEmpty';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { BarcodeScannerModal } from '../various/BarcodeScannerModal';
import { Button } from '../various/Button';

import { useAllProductsContext } from './context/AllProductsContext';
import { ProductsFiltersPanel } from './FiltersBar/FiltersPanel';
import { ProductDetailsModalContextProvider } from './ProductDetails/context/ProductDetailsModalContext';
import { ProductDetailsModal } from './ProductDetails/ProductDetailsModal';
import { NoResults } from './NoResults';
import { ProductsActions } from './ProductsActions';
import { ProductsList } from './ProductsList';
import { ProductsListingHeading } from './ProductsListingHeading';
interface Props {
  productId: string | undefined;
  variantId: string | undefined;
}
export const AllProductsPageContent = ({ productId, variantId }: Props) => {
  const { t } = useTranslation(['common', 'products', 'accounts', 'selections']);
  const isUserLoggedIn = useSelector(getIsLoggedIn);
  const orderDetails = useSelector(getOrderDetails);
  const isProductsGridView = useSelector(getIsProductsGridView);

  const {
    sortedProducts,
    isLoadingProducts,
    fetchNextPage,
    pageCount,
    totalItems,
    isFetchingNextPage,
    productAttributes,
    filters,
    changeFilters,
    productsDetails,
    shouldShowNoResults,
  } = useAllProductsContext();
  const shareUrl = createAbsoluteUrl(useAllProductsLink());
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = React.useState(false);
  const { trackEvent } = useAmplitude();

  React.useEffect(() => {
    trackEvent({ name: isProductsGridView ? 'listing.view.grid' : 'listing.view.list' });
  }, [isProductsGridView, trackEvent]);

  const loadMoreProducts = React.useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  if (isEmpty(orderDetails.account)) {
    const redirectPath = isUserLoggedIn ? paths.ROOT : paths.LOGIN;

    return (
      <div className="p-12 text-center">
        {t('accounts:account_not_active')}
        <Button to={redirectPath} as={Link}>
          {t('common:home_page')}
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="relative flex-1 sm:ml-6 lg:ml-[18rem]">
        <ProductsFiltersPanel
          onDrawerClose={() => setIsFiltersDrawerOpen(false)}
          isDrawerOpen={isFiltersDrawerOpen}
          productsDetails={productsDetails}
        />
        <div>
          <div className="sticky top-[calc(var(--headerHeight)+var(--breadcrumbsHeight)+1.5rem)] z-topBar w-full bg-neutral-96 pr-4 transition-[top] group-[.scrollingDown]/body:top-[calc(var(--breadcrumbsHeight)+1.5rem)] sm:top-[calc(var(--headerHeight)+var(--breadcrumbsHeight)+1rem)] sm:group-[.scrollingDown]/body:top-[calc(var(--breadcrumbsHeight)+1rem)] lg:pr-8">
            <ProductsListingHeading
              isLoading={isLoadingProducts}
              filters={filters}
              onFiltersOpen={() => setIsFiltersDrawerOpen(true)}
              attributes={productAttributes}
              shareUrl={shareUrl}
              onFilterChange={changeFilters}
            >
              <ProductsActions />
            </ProductsListingHeading>
          </div>
          {shouldShowNoResults ?
            <NoResults filters={filters} onFilterChange={changeFilters} />
          : <ProductsList
              isLoadingProducts={isLoadingProducts}
              products={sortedProducts}
              onLoadMore={loadMoreProducts}
              isLoadingMoreProducts={isFetchingNextPage}
              totalItems={totalItems}
              pageCount={pageCount}
              getProductUrl={getUrlForProductAndVariant}
          />
          }
        </div>
      </div>
      <BarcodeScannerModal />
      <ProductDetailsModalContextProvider
        productId={productId}
        products={sortedProducts}
        isLoadingMoreProducts={isLoadingProducts}
        fetchNextPage={fetchNextPage}
        variantId={variantId}
      >
        <ProductDetailsModal modalLocation="products" />
      </ProductDetailsModalContextProvider>
    </>
  );
};
